import { ActionIcon, Group, ScrollArea } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { formatDate, STANDARD_DATE_FORMATS } from '../../../../helpers/date-time-helpers';
import {
  inkDarkest,
  inkLight,
  inkLighter,
  secondaryBase,
  skyDark,
  skyLightest,
} from '../../../../styles/design-tokens';
import { Divider } from '../../../../ui-library/divider/divider';
import { Icon } from '../../../../ui-library/icon/icon';
import { Text } from '../../../../ui-library/typography/typography';
import { DIVIDER_MARGIN, MARKDOWN_COMPONENTS, PR_STATE_STYLES, REMARK_PLUGINS } from './pr-comment-panel.constants';
import {
  buildCommentItems,
  buildPrSubtitle,
  contributorName,
  resolveActiveCommentIndex,
  resolveNeighbourIndices,
  safeHttpUrl,
} from './pr-comment-panel.helpers';
import {
  Added,
  ArrowSpacer,
  Body,
  Branches,
  CodeContext,
  CommentCard,
  DetailsNote,
  EmptyBody,
  ExternalLink,
  FilePath,
  MarkdownBody,
  Position,
  PrMeta,
  Removed,
  ReplyChip,
  Repo,
  StateBadge,
  Wrapper,
} from './pr-comment-panel.styles';
import type { PrFullComment } from '../../../../api/unified-process-graph-client/unified-process-graph-client.type';
import type { PrCommentPanelProps } from './pr-comment-panel.type';

/**
 * Side panel for PR review comments on the unified process graph.
 *
 * Responsibilities:
 * 1. Summarize the PR in the header: number and author, repository, state,
 *    size, commit count, and opened / merged dates
 * 2. Show one comment at a time as Markdown, with the review it belongs to
 * 3. Step through every comment on the PR with arrows, selecting the
 *    comment's node on the graph whenever the arrows cross into another review
 *
 * The graph only carries trimmed previews, so the panel shows those at once and
 * swaps in the full bodies, the PR title and link, branches, and each comment's
 * line and code context from the PR comments endpoint once they load. If that
 * request fails it keeps the previews and says so.
 *
 * Comment bodies are user-written. They render through react-markdown with no
 * `rehype-raw`, so raw HTML is shown as text, never as markup.
 *
 * @param {PrCommentPanelProps} props - The opened node, its PR, the PR's comment nodes, and callbacks
 * @returns {JSX.Element} The rendered comment panel
 */
export function PrCommentPanel({
  comment,
  pr,
  prComments,
  onSelectComment,
  onClose,
  details,
  isLoadingDetails = false,
  isDetailsError = false,
}: PrCommentPanelProps) {
  const { t } = useTranslation();
  const [activeDetailId, setActiveDetailId] = useState<string | null>(null);

  const closeButton = (
    <ActionIcon
      variant="subtle"
      size="sm"
      radius="xl"
      color={skyDark}
      onClick={onClose}
      aria-label={t('common.actions.close')}
    >
      <Icon name="close" size={18} color={skyDark} />
    </ActionIcon>
  );

  if (!comment) {
    return (
      <Wrapper>
        <Body>
          <Group justify="flex-end">{closeButton}</Group>
          <Text size="small" color={inkLight} style={{ marginTop: 12 }}>
            These comments are no longer available.
          </Text>
        </Body>
      </Wrapper>
    );
  }

  const items = buildCommentItems(prComments);
  const activeIndex = resolveActiveCommentIndex(items, activeDetailId, comment.id);
  const active = activeIndex >= 0 ? items[activeIndex] : undefined;
  const { previousIndex, nextIndex } = resolveNeighbourIndices(items, prComments, comment.id, activeIndex);
  const hasPrevious = previousIndex >= 0;
  const hasNext = nextIndex >= 0 && nextIndex < items.length;
  const goTo = (index: number) => {
    const target = items[index];
    if (!target) return;
    setActiveDetailId(target.detail.id);
    if (target.node.id !== comment.id) onSelectComment(target.node.id);
  };

  const totalOnNode = comment.loop_count ?? comment.count;
  const fullChangeRequest = details?.change_request;
  const fullById = new Map<string, PrFullComment>((details?.comments ?? []).map((full) => [full.id, full]));
  const activeFull = active ? fullById.get(active.detail.id) : undefined;
  const activeBody = activeFull?.body ?? active?.detail.body ?? '';
  const activeAuthor = contributorName(activeFull?.author) ?? active?.detail.author ?? 'Unknown author';
  const activeLocation = active?.detail.path
    ? `${active.detail.path}${activeFull?.line != null ? `:${activeFull.line}` : ''}`
    : null;
  const prState = fullChangeRequest?.state ?? pr?.state;
  const prUrl = safeHttpUrl(fullChangeRequest?.html_url);
  const prSubtitle = buildPrSubtitle(fullChangeRequest);
  const stateStyle = prState ? PR_STATE_STYLES[prState.toLowerCase()] : undefined;
  const formatWhen = (value: string) => formatDate(value, { format: STANDARD_DATE_FORMATS.DATE_TIME_24H });

  const commentNavigator = (
    <Group gap={2} align="center" wrap="nowrap">
      {/* An arrow with nowhere to go is removed, not disabled. A same-size
                  spacer keeps the counter from jumping as arrows come and go. */}
      {hasPrevious ? (
        <ActionIcon
          variant="subtle"
          size="sm"
          radius="xl"
          color={skyDark}
          onClick={() => goTo(previousIndex)}
          aria-label="Previous comment"
        >
          <Icon name="chevron_left" size={18} color={skyDark} pointer />
        </ActionIcon>
      ) : (
        <ArrowSpacer aria-hidden="true" />
      )}
      <Position aria-live="polite">
        {active ? `${activeIndex + 1} of ${items.length}` : `– of ${items.length}`}
      </Position>
      {hasNext ? (
        <ActionIcon
          variant="subtle"
          size="sm"
          radius="xl"
          color={skyDark}
          onClick={() => goTo(nextIndex)}
          aria-label="Next comment"
        >
          <Icon name="chevron_right" size={18} color={skyDark} pointer />
        </ActionIcon>
      ) : (
        <ArrowSpacer aria-hidden="true" />
      )}
    </Group>
  );

  const commentContent = (
    <>
      <Group justify="space-between" align="center" wrap="nowrap" gap={6}>
        <Group gap={6} align="center" wrap="nowrap" style={{ minWidth: 0 }}>
          <Text size="small" weight="medium" color={inkDarkest}>
            {activeAuthor}
          </Text>
          {activeFull?.in_reply_to_id != null && <ReplyChip>reply</ReplyChip>}
        </Group>
      </Group>
      {activeLocation && <FilePath title={activeLocation}>{activeLocation}</FilePath>}
      {activeBody.trim() ? (
        <MarkdownBody data-markdown="true">
          <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={MARKDOWN_COMPONENTS}>
            {activeBody}
          </ReactMarkdown>
        </MarkdownBody>
      ) : (
        <EmptyBody>No text</EmptyBody>
      )}
      {activeFull?.diff_hunk && (
        <CodeContext>
          <summary>Code context</summary>
          <pre>{activeFull.diff_hunk}</pre>
        </CodeContext>
      )}
      {!activeFull && isLoadingDetails && <DetailsNote>Loading the full comment…</DetailsNote>}
      {!activeFull && isDetailsError && (
        <DetailsNote>Showing a preview; the full comment couldn't be loaded.</DetailsNote>
      )}
    </>
  );

  return (
    <Wrapper>
      {/* Scrollbar hidden to match the session and turn panels; it still scrolls. */}
      <ScrollArea style={{ flex: 1, minHeight: 0 }} type="never">
        <Body>
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Group gap={8} align="center" wrap="nowrap" style={{ minWidth: 0 }}>
              <Icon name="call_merge" size={20} color={secondaryBase} />
              <Text weight="bold" size="medium" color={secondaryBase}>
                {fullChangeRequest?.title || pr?.label || 'Pull request'}
              </Text>
            </Group>
            {closeButton}
          </Group>

          {pr && (
            <PrMeta>
              {prSubtitle && (
                <Text size="small" color={inkLight}>
                  {prSubtitle}
                </Text>
              )}
              <Group gap={6} align="center" wrap="wrap">
                {pr.repository_name && <Repo title={pr.repository_name}>{pr.repository_name}</Repo>}
                {prState && (
                  <StateBadge
                    style={{
                      color: stateStyle?.color ?? inkLight,
                      background: stateStyle?.background ?? skyLightest,
                    }}
                  >
                    {prState.toLowerCase()}
                  </StateBadge>
                )}
                {fullChangeRequest?.draft && (
                  <StateBadge style={{ color: inkLight, background: skyLightest }}>draft</StateBadge>
                )}
              </Group>
              {fullChangeRequest?.source_branch && (
                <Branches title={`${fullChangeRequest.source_branch} → ${fullChangeRequest.target_branch ?? ''}`}>
                  {fullChangeRequest.source_branch}
                  {fullChangeRequest.target_branch && ` → ${fullChangeRequest.target_branch}`}
                </Branches>
              )}
              <Text size="small" color={inkLight}>
                {pr.additions != null && <Added>+{pr.additions}</Added>}
                {pr.deletions != null && <Removed> −{pr.deletions}</Removed>}
                {(pr.additions != null || pr.deletions != null) && ' · '}
                {pr.commits.length} commit{pr.commits.length !== 1 ? 's' : ''}
              </Text>
              <Text size="small" color={inkLighter}>
                Opened {formatWhen(pr.timestamp)}
                {pr.merged_at && ` · Merged ${formatWhen(pr.merged_at)}`}
              </Text>
              {prUrl && (
                <ExternalLink href={prUrl} target="_blank" rel="noopener noreferrer">
                  View on GitHub
                  <Icon name="open_in_new" size={12} color={secondaryBase} pointer />
                </ExternalLink>
              )}
            </PrMeta>
          )}

          <Divider style={DIVIDER_MARGIN} />

          <Group justify="space-between" align="center" wrap="nowrap">
            <div style={{ minWidth: 0 }}>
              <Text size="small" weight="bold" color={inkDarkest}>
                {comment.label}
              </Text>
              <Text size="small" color={inkLighter}>
                {formatWhen(comment.timestamp)}
              </Text>
            </div>
            {commentNavigator}
          </Group>

          {active ? (
            <CommentCard>{commentContent}</CommentCard>
          ) : (
            <Text size="small" color={inkLight} style={{ marginTop: 10 }}>
              No comment text was recorded for this review.
            </Text>
          )}

          {comment.comments.length > 0 && comment.comments.length < totalOnNode && (
            <Text size="small" color={inkLighter} style={{ marginTop: 10 }}>
              This review has {totalOnNode} comments; the text of {comment.comments.length} is available.
            </Text>
          )}
        </Body>
      </ScrollArea>
    </Wrapper>
  );
}
